import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../component/element/table";
import ItemAction from "../../../component/ui/action/ItemAction";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  delete_user,
  get_users,
  toggle_user_status,
} from "../../../services/apis/users";
import { IUser } from "../../../store/types/users";
import UserFormModal from "./UserFormModal";

interface UsersTableProps {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  createOpen: boolean;
  onCreateClose: () => void;
}

const UsersTable = ({
  page,
  setPage,
  createOpen,
  onCreateClose,
}: UsersTableProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [editingUser, setEditingUser] = React.useState<IUser | null>(null);
  const { users, total, limit, loading, loadingUserId } = useAppSelector(
    ({ user }) => user,
  );
  const lastPage = Math.max(1, Math.ceil(total / limit));

  React.useEffect(() => {
    if (page > lastPage) {
      setPage(lastPage);
      return;
    }
    void get_users(dispatch, (page - 1) * limit, limit);
  }, [dispatch, lastPage, limit, page, setPage]);

  const columns: ColumnDef<IUser>[] = [
    {
      accessorKey: "profile.name",
      header: "Name",
      cell: ({ getValue }) => (
        <span className="font-medium">{(getValue() as string) || "—"}</span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "profile.phone",
      header: "Phone number",
      cell: ({ getValue }) => (getValue() as string) || "—",
    },
    {
      accessorKey: "is_elite",
      header: "Membership",
      cell: ({ getValue }) => {
        const isElite = getValue() as boolean;
        return (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              isElite
                ? "bg-amber-100 text-amber-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {isElite ? "Elite" : "Standard"}
          </span>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: "Account",
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const { id, is_active: isActive } = row.original;
        const isUpdating = loadingUserId === id;
        return (
          <ItemAction
            show_view
            show_edit
            show_delete
            show_toggle
            toggle_checked={isActive}
            toggle_loading={isUpdating}
            delete_loading={isUpdating}
            onView={() => navigate(`/user/${id}`)}
            onEdit={() => setEditingUser(row.original)}
            onDelete={async () => {
              if (
                !window.confirm("Are you sure you want to delete this user?")
              ) {
                return;
              }
              const deleted = await delete_user(dispatch, id);
              if (!deleted) return;

              const nextPage = Math.min(
                page,
                Math.max(1, Math.ceil((total - 1) / limit)),
              );
              if (nextPage !== page) {
                setPage(nextPage);
              } else {
                await get_users(dispatch, (page - 1) * limit, limit);
              }
            }}
            onToggle={(checked) =>
              void toggle_user_status(dispatch, id, checked)
            }
          />
        );
      },
    },
  ];

  return (
    <>
      {loading && (
        <p className="mb-2 text-sm text-gray-500" role="status">
          Loading users...
        </p>
      )}
      <DataTable
        columns={columns}
        data={users}
        current_page={page}
        last_page={lastPage}
        onNext={() => page < lastPage && setPage(page + 1)}
        onPrev={() => page > 1 && setPage(page - 1)}
      />
      <UserFormModal
        open={createOpen || editingUser !== null}
        user={editingUser}
        onClose={() => {
          setEditingUser(null);
          onCreateClose();
        }}
      />
    </>
  );
};

export default UsersTable;
